import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { getItem, removeItem, setItem } from '~/services/localStorage';

interface FormStorageOptions {
  key: string;
  debounceTime?: number;
  conditionKey?: string;
}

// eslint-disable-next-line @typescript-eslint/ban-types
const useFormStorage = <T extends object>(
  form: UseFormReturn<T>,
  options: FormStorageOptions,
  defaultValues = {} as T,
): { clearDraft: () => void; isRestored: boolean } => {
  const { key, debounceTime = 1000, conditionKey } = options;
  const { watch, reset, setValue } = form;
  const [isRestored, setIsRestored] = useState(false);

  const defaultValuesRef = useRef(defaultValues);
  useEffect(() => {
    defaultValuesRef.current = defaultValues;
  }, [defaultValues]);

  const formData = watch();

  const saveToLocalStorage = useCallback(() => {
    setItem(key, formData);
  }, [key, formData]);

  const needRestore = useMemo(() => {
    const storedData = getItem(key);

    if (!conditionKey || !storedData) {
      return true;
    }

    return (
      conditionKey && storedData[conditionKey] === (formData as typeof storedData)?.[conditionKey]
    );
  }, [formData, key, conditionKey]);

  // Restore form data from local storage on component mount
  useEffect(() => {
    const storedData = getItem(key);
    if (storedData && needRestore) {
      try {
        const parsedData = storedData;

        reset({ ...defaultValuesRef.current, ...parsedData });
        // need to set form isDirty true for the navigation confirmation popup
        setValue(undefined, undefined, { shouldDirty: true });
        setIsRestored(true);
      } catch (e) {
        removeItem(key);
      }
    } else {
      // Set isRestored to true even if nothing was restored,
      // so the consuming component knows the initial checks are complete.
      setIsRestored(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, needRestore, isRestored, setValue, reset]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      saveToLocalStorage();
    }, debounceTime);

    return () => clearTimeout(timerId);
  }, [saveToLocalStorage, debounceTime]);

  const clearDraft = () => {
    removeItem(key);
    setIsRestored(false);
  };

  return { isRestored, clearDraft };
};

export default useFormStorage;
